import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Loading from './Loading';
import Post from './Post';
import { trackEventIfConsented } from './analyticsConsent';
import { trackInternalNavigationIfConsented, usePageGroupTracking } from './pageAnalytics';
import { DEFAULT_POST_PAGE_SIZE, fetchPostsPage } from './postData';
import { getSetupPageData, parseListTags } from './setupContent';
import { usePageSeo } from './seo';

const LatestPage = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  usePageSeo({
    title: 'Latest desk setups',
    description:
      'Browse the latest real desk setups on 101Setups, then jump into topical hubs for cable management, monitor arms, lighting, and more.',
    canonicalPath: '/latest',
  });
  usePageGroupTracking('latest_page', {
    page_role: 'latest_feed',
  });

  useEffect(() => {
    let isMounted = true;

    const loadPosts = async () => {
      setLoading(true);
      const latestPosts = await fetchPostsPage({ page: pageIndex, limit: DEFAULT_POST_PAGE_SIZE });

      if (!isMounted) {
        return;
      }

      setPosts(latestPosts);
      setHasMore(latestPosts.length === DEFAULT_POST_PAGE_SIZE);
      setLoading(false);
    };

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, [pageIndex]);

  if (loading) {
    return <Loading />;
  }

  return (
    <section className="page-shell">
      <header className="page-intro">
        <p className="page-kicker">Community stream</p>
        <h1>Latest desk setups</h1>
        <p>
          The feed still exists, but it now sits on its own route so the homepage can focus on discovery and search-ready
          navigation. Browse the newest setups here, then open topic hubs for more structured exploration.
        </p>
        <div className="page-actions">
          <Link
            className="home-hero-link primary"
            to="/desk-setup-ideas"
            onClick={() => {
              trackEventIfConsented('hero_cta_click', {
                cta_name: 'explore_desk_setup_ideas',
                page_context: 'latest_page',
              });
              trackInternalNavigationIfConsented({
                link_target: '/desk-setup-ideas',
                link_context: 'latest_primary_cta',
                source_page_group: 'latest_page',
                destination_page_group: 'search_hub',
              });
            }}
          >
            Explore desk setup ideas
          </Link>
          <a
            className="home-hero-link"
            href="/#newsletter"
            onClick={() => {
              trackEventIfConsented('hero_cta_click', {
                cta_name: 'join_newsletter',
                page_context: 'latest_page',
              });
              trackInternalNavigationIfConsented({
                link_target: '/#newsletter',
                link_context: 'latest_secondary_cta',
                source_page_group: 'latest_page',
                destination_page_group: 'footer_newsletter',
              });
            }}
          >
            Get weekly inspiration
          </a>
        </div>
      </header>

      <div className="post-list-grid page-section">
        {posts.map(model => {
          const setup = getSetupPageData(model);

          return (
            <Post
              key={model.id}
              id={model.id}
              userID={model.userid}
              imgURL={model.imgURL}
              listTags={parseListTags(model.listtags)}
              title={setup.title}
              summary={setup.excerpt}
              categoryLabel={setup.primaryCategory?.shortLabel}
              canonicalPath={setup.path}
              sharePath={setup.path}
              altText={setup.altText}
              pageContext="latest_feed"
            />
          );
        })}
      </div>

      <div className="pager-buttons page-section">
        <button type="button" className="pager-button" onClick={() => setPageIndex(pageIndex - 1)} disabled={pageIndex === 0}>
          Newer
        </button>
        <span className="pager-status">Page {pageIndex + 1}</span>
        <button type="button" className="pager-button" onClick={() => setPageIndex(pageIndex + 1)} disabled={!hasMore}>
          Older
        </button>
      </div>
    </section>
  );
};

export default LatestPage;
